import { useAppDispatch, useAppSelector } from '@/Hook/hooks';
import { clearAllModel, getDashboardBasicData, loadWidgetGroup, saveUserDashboardLayout, setFieldFilter, setWidgetModelValue } from '@/Redux/Ducks/dashboardSlice';
import { setDataAnalyticsModelValue } from '@/Redux/Ducks/dataAnalyticsSlice';
import { EHelpOpenIn } from '@/Redux/Ducks/helpSlice';
import Information from '@/UI/BaseComponents/Information';
import Map from '@/UI/BaseComponents/Map';
import Table from '@/UI/BaseComponents/Table';
import { ICriteriaFieldConfig } from '@/UI/Interfaces/IFieldConfig';
import { AlignInput, Chart, ControlTranslation, Icon, Modal } from 'AlignComponents';
import { useEffect, useState } from 'react';
import { Badge, Button, ButtonGroup, Dropdown, DropdownButton } from 'react-bootstrap';
import RGL, { WidthProvider } from "react-grid-layout";
import { FaCheckCircle, FaFilter } from 'react-icons/fa';
import DashboardSelector from './DashboardSelector';
import "react-grid-layout/css/styles.css"
import classNames from 'classnames';
import { filterOption } from '@/Utils/constants';
import BaseMethods from 'AlignBaseUI/BaseMethods';
import { setActiveTab } from '@/Redux/Ducks/tabSlice';
import { GrClear } from 'react-icons/gr';
import { setGlobalMessage } from '@/Redux/Ducks/messageSlice';

const GridLayout = WidthProvider(RGL);

const Dashboard = () => {
	const dispatch = useAppDispatch();
	const { isGroupsLoaded, activeWidget } = useAppSelector(x => ({
		isGroupsLoaded: x.Dashboard.isGroupsLoaded,
		activeWidget: x.Dashboard.activeWidget
	}));
	const [layout, setLayout] = useState<any>('[]');
	const [criteriaModal, toggleCriteriaModal] = useState<boolean>(false);

	useEffect(() => {
		dispatch(setActiveTab(activeWidget.widgetgroupno))
	}, []);

	useEffect(() => {
		if (!isGroupsLoaded)
			dispatch(getDashboardBasicData());
	}, [isGroupsLoaded]);

	useEffect(() => {
		if (activeWidget.layout.length >= 1) {
			var dashboardLayout = activeWidget.layout.find(x => x.insightarea == 'D');
			var defaultLayout = dashboardLayout ? dashboardLayout.layout : activeWidget.layout.find(x => x.insightarea == 'A')?.layout as string;
			setLayout(defaultLayout);
		}
	}, [activeWidget])

	const BaseFieldStateSet = (fieldId: string, newValue: any, helpSelected: boolean = false, toValue: boolean = false) => {
		dispatch(setWidgetModelValue({ fieldId, newValue, toValue }));
	};

	const BaseSetFieldFilter = (fieldId: string, filterType: string, caseSensitive: boolean) => {
		dispatch(setFieldFilter({ fieldId, filterType, caseSensitive }));
		document.querySelectorAll(`.filter-container .dropdown-menu`).forEach((x) => x.classList.remove("show"))
	};

	const criteriaField = (field: ICriteriaFieldConfig, index: number) => {
		const fieldModel = activeWidget.modelValue[field.fieldid];
		return (
			<div className="d-flex align-items-end" key={index}>
				<AlignInput
					key={`from-${field.fieldid}`}
					fieldId={field.fieldid}
					config={{ FieldConfig: [field], BaseFieldStateSet }}
					customFieldValue={fieldModel.fromValue || null}
					customFieldVisible={true}
					customFieldEnable={true}
					hideLabel={true}
					showPlaceholder={true}
					customFieldHelpObject={field.helpobjectno || ""}
					customFieldHelpWhere={field.helpwhere || ""}
					customValidateMandatory={field.validatemandatory}
					customHyperLinkJump={field.hyperlinkjump || ""}
					custom={true}
					uniqueKey="from"
					customHelpParams={{
						helpOpenIn: EHelpOpenIn.Dashboard,
						multiSelect: field.multiplevalue
					}}
				/>
				{
					field.filteroption ?
						<div className="filter-container">
							<DropdownButton
								className="ms-1"
								variant="filter"
								as={ButtonGroup}
								title={<Icon icon="Filter" size="14" color="#575757" className="mx-1" />}
								onClick={() => {
									document.querySelectorAll(`.filter-container .dropdown-menu:not(#filter-option-${field.fieldid})`).forEach((x) => x.classList.remove("show"))
									document.getElementById(`filter-option-${field.fieldid}`)?.classList.toggle("show")
								}}>
								{
									filterOption.filter(x => !x.filterSymbol.isEqual("cs") && x.fieldType.includes(BaseMethods.getControlType(field.controltypeno))).map((x, i) => (
										<Dropdown.Item
											key={x.filterSymbol}
											className={classNames({ active: x.filterSymbol == fieldModel.filterType })}
											role="button"
											onClick={() => BaseSetFieldFilter(field.fieldid, x.filterSymbol, fieldModel.caseSensitive)}
										>
											{x.filterText}
										</Dropdown.Item>
									))
								}
								<Dropdown.Divider />
								{
									filterOption.filter(x => x.filterSymbol.isEqual("cs") && x.fieldType.includes(BaseMethods.getControlType(field.controltypeno))).map((x, i) => (
										<Dropdown.Item
											key={x.filterSymbol}
											className={classNames({ active: fieldModel.caseSensitive })}
											role="button"
											onClick={() => BaseSetFieldFilter(field.fieldid, fieldModel.filterType, !fieldModel.caseSensitive)}
										>
											{x.filterText}
										</Dropdown.Item>
									))
								}
							</DropdownButton>
						</div>
						: null
				}

				<span className="to-value">
					{
						fieldModel.filterType.isEqual("not between", "between") ?
							<AlignInput
								key={`to-${field.fieldid}`}
								fieldId={field.fieldid}
								hideLabel={true}
								showPlaceholder={true}
								config={{ FieldConfig: [field], BaseFieldStateSet: (fieldId: string, newValue: any, helpSelected: boolean) => BaseFieldStateSet(fieldId, newValue, helpSelected, true) }}
								customFieldValue={fieldModel.toValue || null}
								customFieldVisible={true}
								customFieldEnable={true}
								customFieldHelpObject={field.helpobjectno || ""}
								customFieldHelpWhere={field.helpwhere || ""}
								customValidateMandatory={field.validatemandatory}
								customHyperLinkJump={field.hyperlinkjump || ""}
								custom={true}
								uniqueKey="to"
								customHelpParams={{
									helpOpenIn: EHelpOpenIn.Dashboard
								}}
							/>
							: null
					}
				</span>
			</div>
		);
	}

	const routeToScreen = () => {

	}

	const loadFunc = () => {
		activeWidget.criteria.map(x => {
			if (x.validatemandatory) {
				if (!activeWidget.modelValue[x.fieldid].fromValue) {
					dispatch(setGlobalMessage({ error: new Error(`Please fill Mandatory field ${x.fieldlabel}`) }));
					throw null;
				}
			}
		});

		dispatch(loadWidgetGroup(activeWidget.widgetgroupno))
	}

	const openCriteriaModal = () => {
		toggleCriteriaModal(true);
	}

	const closeCriteriaModal = () => {
		toggleCriteriaModal(false);
	}

	const onLayoutChange = (newLayout: any) => {
		setLayout(JSON.stringify(newLayout));
	}

	const saveLayout = () => {
		dispatch(saveUserDashboardLayout({ widgetgroupno: activeWidget.widgetgroupno, layout, insightarea: 'D' }));
	}

	const clearAll = () => {
		dispatch(clearAllModel());
	}

	return (
		<>
			<Modal
				close={closeCriteriaModal}
				heading={<ControlTranslation system={true} controlId="lblcriteria" />}
				show={criteriaModal}
				bodyStyle={{ minHeight: "350px" }}
				modalSize="xl"
				footer={
					<>
						<Button
							variant="light"
							onClick={closeCriteriaModal}>
							close
						</Button>
						<Button
							variant="primary"
							onClick={loadFunc}>
							Apply Filter
						</Button>
					</>
				}
			>
				<div className="criteria-fields">
					{
						[...activeWidget.criteria, ...activeWidget.FICriteria].length >= 1 ? (
							<div className="container-fluid">
								<div className="row">
									{
										activeWidget.criteria.map((x, i) => (
											criteriaField(x, i)
										))
									}
								</div>
								{
									activeWidget.FICriteria.length ?
										<>
											<span className="group-label">Financial Dimensions</span>
											<div className="row">
												{
													activeWidget.FICriteria.map((x, i) => (
														criteriaField(x, i)
													))
												}
											</div>
										</>
										: null
								}
							</div>
						) : null
					}
				</div>
			</Modal>
			<div className="dashboard">
				<div className="dashboard-top-bar">
					{
						Object.entries(activeWidget.modelValue).map(([fieldId, model], idx) => {
							let criteriaConf = activeWidget.criteria.find(y => y.fieldid == fieldId) as ICriteriaFieldConfig;

							if (model.fromValue || model.toValue) {
								return (
									<span key={idx} className="badge bg-light border text-black fs-10 rounded-pill">
										{model.fromValue}
										{
											criteriaConf.validatemandatory ? (
												<span style={{ color: 'red', marginLeft: '5px' }}>*</span>
											) : null
										}
									</span>
								)
							}
							else return null;
						})
					}

					<a role="button" className="badge bg-light border text-black fs-10 rounded-pill" onClick={clearAll}>
						<GrClear size={10} />
					</a>

					<div className="ms-auto" />
					{
						activeWidget.criteria.length >= 1 ? (
							<Button
								variant="light"
								onClick={openCriteriaModal}>
								<FaFilter size={14} />
							</Button>
						) : null
					}

					<DashboardSelector />
					<Button
						variant="light"
						onClick={saveLayout}>Save Layout</Button>
					<Button
						variant="primary"
						onClick={loadFunc}>
						Load
					</Button>
				</div>
				{
					activeWidget.objectsLoaded ?
						(
							activeWidget.objects.length >= 1 ? (
								<GridLayout
									layout={JSON.parse(layout)}
									cols={12}
									rowHeight={50}
									onLayoutChange={onLayoutChange}
									draggableHandle=".drag-handle"
								>
									{
										activeWidget.objects.map(x =>
											x.widgettype == 'INFO' ? (
												<div key={x.widgetno} style={{ overflow: 'hidden' }}>
													<Information widgetno={x.widgetno} />
												</div>
											) : x.widgettype == 'CHRT' ? (
												<div key={x.widgetno} style={{ overflow: 'hidden' }}>
													<Chart widgetno={x.widgetno} />
												</div>
											) : x.widgettype == 'TBLE' ? (
												<div key={x.widgetno}>
													<Table widgetno={x.widgetno} />
												</div>
											) : x.widgettype == 'MAP' ? (
												<div key={x.widgetno} style={{ overflow: 'hidden' }}>
													<Map detail={x.data} widgetlabel={x.widgetstxt} widgetprocesscode={x.widgetprocesscode} widget={x} routeToScreen={routeToScreen} />
												</div>
											) : null
										)
									}
								</GridLayout>
							) : (
								<div className="text-group">
									<FaCheckCircle size="40" />
									<p className="mb-0"><ControlTranslation system={true} controlId="lbldashboardnotconfigured" /></p>
									<span><ControlTranslation system={true} controlId="lblcontactadministrator" /></span>
								</div>
							)
						) : (
							<div className="text-group">
								<FaCheckCircle size="40" />
								<p className="mb-0"><ControlTranslation system={true} controlId="lblwait" /></p>
								<span><ControlTranslation system={true} controlId="lblloadingdashboard" /></span>
							</div>
						)
				}
			</div>
		</>
	)
}

export default Dashboard